//This is a replicate of contactNoteService with updated api
import { EnvConfig } from 'config/envConfig';
import { add, parseISO } from 'date-fns';
import { ContactNoteDialog } from 'types/ContactNote';
import { CN_MGMT_API_URL, PARTNER_MGMT_API_URL, PSH_BACKEND_URL } from 'services/constants';
import removeEmptyProperties from 'utils/requestUtil';
import { assertNotNullOrUndefined } from 'utils/utils';
import { serialiseData } from 'utils/noteDialogUtil';
import { FileInfo } from 'views/ContactNoteForm/components/DocumentUpload/DocumentDropZone';
import { axiosInstance } from '../shared/axios/axiosConfig';
import { getHostBaseUrl } from 'utils/browserUtil';

export interface QualifiedStatusResponse {
  status: 'N/A' | 'true' | 'false';
}

/**
 * Call the controller to create a new contact note
 */
const create = async (newNote: ContactNoteDialog) => {
  const updatedNote = serialiseData(newNote);
  // Create a filtered copy of the contact note object
  const filteredNote = Object.fromEntries(
    Object.entries(updatedNote).filter(([_, value]) => value !== '' && value !== null),
  );
  delete filteredNote.startDate;
  delete filteredNote.startTime;
  delete filteredNote.endDate;
  delete filteredNote.endTime;

  const formData = new FormData();

  const attachmentBlobs = await Promise.all(
    ((filteredNote.attachments as FileInfo[]) ?? []).map(async (attachment) => {
      if (!attachment.b64Data) return null;
      const blob = attachment.file;
      return { blob, name: attachment.name };
    }),
  );

  for (const a of attachmentBlobs) {
    if (a) formData.append('msgFile', a.blob, a.name);
  }
  delete filteredNote.attachments;
  formData.append('note', new Blob([JSON.stringify(filteredNote)], { type: 'application/json' }));
  const host = getHostBaseUrl();
  const response = await fetch(`${host}${CN_MGMT_API_URL}/frontend/notes`, {
    method: 'POST',
    body: formData,
    credentials: 'include',
  });

  if (!response.ok) {
    const errorText = await response.json();
    throw errorText;
  }

  return await response.json();
};

/**
 * Call the controller to modify an existing contact note
 */
// TODO: Remove ContactNote when the existing creation flow is removed
const updateContactNote = async (editedContactNote: ContactNoteDialog) => {
  const data = serialiseData(editedContactNote);
  const updatedNote = data;
  delete updatedNote?.startDate;
  delete updatedNote?.startTime;
  delete updatedNote?.endDate;
  delete updatedNote?.endTime;
  removeEmptyProperties(updatedNote);
  const formData = new FormData();

  const attachmentBlobs = await Promise.all(
    ((updatedNote.attachments as FileInfo[]) ?? []).map(async (attachment) => {
      if (!attachment.b64Data) return null;
      const blob = attachment.file;
      return { blob, name: attachment.name };
    }),
  );

  for (const a of attachmentBlobs) {
    if (a) formData.append('msgFile', a.blob, a.name);
  }

  if (updatedNote.attachments) {
    updatedNote.attachments = updatedNote.attachments.filter((item) => !item.b64Data);
  }

  formData.append('note', new Blob([JSON.stringify(updatedNote)], { type: 'application/json' }));
  const host = getHostBaseUrl();
  const response = await fetch(`${host}${CN_MGMT_API_URL}/frontend/notes/${editedContactNote.id}`, {
    method: 'PUT',
    body: formData,
    credentials: 'include',
  });

  if (!response.ok) {
    const errorText = await response.json();
    throw errorText;
  }

  return await response.json();
};

const deleteContactNote = async (id: string) => {
  const request = axiosInstance.put(`${CN_MGMT_API_URL}/frontend/notes/v2/${id}`);
  const response = await request;
  return response.data;
};

/**
 * Call the controller to get all notes that has as creator the current user
 */
const getMyNotes = async (
  creator?: string | null,
  teamView?: boolean | null,
  query?: string | null,
  dateRange?: any[],
  globalPartnerId?: string | null,
  techId?: string | null,
  usedInBr?: boolean,
) => {
  assertNotNullOrUndefined(dateRange);

  // We need to send the query data via POST to comply with security standards
  return searchNotesAgGridV2({
    query: query,
    start: dateRange[0] || null,
    //We add an extra day to include all dates on the end date.
    end: dateRange[1] ? add(parseISO(dateRange[1]), { hours: 24 }) : null,
    globalPartnerId,
    ...(usedInBr ? { brId: techId } : { partnerId: techId }),
    creator,
    teamView,
  });
};

/**
 * AgGrid v2 server-side search with proper filtering, sorting, and pagination
 * @param agGridRequest AgGrid server-side request object
 * @returns List of contact notes
 */
const searchNotesAgGridV2 = async (agGridRequest: any) => {
  const request = await axiosInstance.post(`${CN_MGMT_API_URL}/frontend/v2/notes/search`, agGridRequest);
  return request.data;
};

const getAuditHistory = async (id: string) => {
  const request = await axiosInstance.get(`${CN_MGMT_API_URL}/audit/notes/${id}`);
  const response = await request;
  return response.data;
};

const getQualifiedContact = async (contactType: string, initiatedBy: string) => {
  const request = await axiosInstance.get(`${CN_MGMT_API_URL}/frontend/notes/qualifier-status`, {
    params: {
      contactType,
      initiatedBy,
    },
  });
  const response = await request;
  return response.data;
};

const getEmployeeOptions = async () => {
  const response = await axiosInstance.get(`${CN_MGMT_API_URL}/internal/v1/employees`);
  // return mockEmployeeOptionsList;
  return response.data;
};

const getDropdownOptions = async () => {
  try {
    const [initiatedByOptions, contactTypeOptions, topicsOptions] = await Promise.all([
      axiosInstance.get(`${CN_MGMT_API_URL}/frontend/dropdowns/INITIATED_BY`),
      axiosInstance.get(`${CN_MGMT_API_URL}/frontend/dropdowns/CONTACT_TYPE`),
      axiosInstance.get(`${CN_MGMT_API_URL}/frontend/dropdowns/TOPIC`),
    ]);
    return [initiatedByOptions.data, contactTypeOptions.data, topicsOptions.data];
  } catch {
    return [[], [], []];
  }
};

const getTopicDropdownValues = async (fromTopics = false) => {
  const response = await axiosInstance.get(`${CN_MGMT_API_URL}/frontend/dropdowns/TOPIC`, {
    params: { fromTopics },
  });
  return response.data;
};

const getSupportedAttachmentFormats = async (): Promise<string[]> => {
  const response = await axiosInstance.get(`${CN_MGMT_API_URL}/frontend/notes/attachments/supported-formats`);
  return response.data;
};

const getOrganisationalUnitValues = async () => {
  const response = await axiosInstance.get(`${CN_MGMT_API_URL}/frontend/dropdowns/ORGANISATIONAL_UNIT`);
  return response.data;
};

const getRelatedBrs = async (partnerUid: string) => {
  const request = await axiosInstance.get(`${CN_MGMT_API_URL}/frontend/relatedBrs/${partnerUid}`);
  const response = await request;
  return response.data;
};
const getPartnerDetails = async (partnerUid: string) => {
  const request = await axiosInstance.get(`${PARTNER_MGMT_API_URL}/partners/${partnerUid}`);
  const response = await request;
  return response.data;
};

const getBRPartnerDetails = async (partnerUids: string[]) => {
  const request = await axiosInstance.post(`${CN_MGMT_API_URL}/frontend/partners`, partnerUids);
  const response = await request;
  return response.data;
};

const getMultiplePartnerDetails = async (uidList: string[]) => {
  const request = await axiosInstance.post(`${PARTNER_MGMT_API_URL}/v2/partners/multiple-read`, uidList);
  const response = await request;
  return response.data;
};

const sendNotification = async ({
  userId,
  partnerUID,
  brUID,
  contactNoteId,
}: {
  userId: string[];
  partnerUID?: string;
  brUID?: string;
  contactNoteId?: string;
}) => {
  const request = await axiosInstance.post(`${CN_MGMT_API_URL}/frontend/notes/notification`, {
    userId,
    partnerUID,
    brUID,
    contactNoteId,
    groupName: null,
  });
  const response = await request;
  return response.data;
};

const getBRLinkedClientRepresentatives = async (brUid: string) => {
  const request = await axiosInstance.get(`${CN_MGMT_API_URL}/frontend/partners-of-br/${brUid}`);
  const response = await request;
  return response.data;
};

const getClientRepresentative = async (text: string, signal?: AbortSignal) => {
  const query = {
    terms: [
      EnvConfig.config.enablePshApiUpdate
        ? {
            field: 'name',
            values: [text],
          }
        : {
            field: 'name',
            op: 'EQ',
            val: `${text}`,
          },
    ],
  };
  const request = await axiosInstance.get(
    `${PSH_BACKEND_URL}/v1/core/partners?query=${encodeURIComponent(JSON.stringify(query))}&maxPageSize=50`,
    { signal },
  );
  const response = await request;
  return response.data;
};

const getBusinessRelationship = async (brUid: string) => {
  const request = axiosInstance.get(`${CN_MGMT_API_URL}/frontend/business-relationships/${brUid}`);
  const response = await request;
  return response.data;
};

const getRelatedBRForMultiplePartners = async (partnerUids: string[]) => {
  const request = axiosInstance.post(`${CN_MGMT_API_URL}/frontend/relatedBrs`, partnerUids);
  const response = await request;
  return response.data;
};

/**
 * Fetch a single contact note by ID
 * @param id The ID of the contact note to fetch
 * @returns Contact note details
 */
const getContactNoteById = async (id: string) => {
  const request = await axiosInstance.get(`${CN_MGMT_API_URL}/notes/${id}`);
  return request.data;
};

const contactNoteDialogService = {
  create,
  updateContactNote,
  deleteContactNote,
  getMyNotes,
  searchNotesAgGridV2,
  getEmployeeOptions,
  getAuditHistory,
  getQualifiedContact,
  getBRPartnerDetails,
  getDropdownOptions,
  getTopicDropdownValues,
  getRelatedBrs,
  getRelatedBRForMultiplePartners,
  getBRLinkedClientRepresentatives,
  getPartnerDetails,
  sendNotification,
  getClientRepresentative,
  getBusinessRelationship,
  getMultiplePartnerDetails,
  getContactNoteById,
  getOrganisationalUnitValues,
  getSupportedAttachmentFormats,
};
export default contactNoteDialogService;
